import React, { useState, useEffect, useCallback } from 'react';
import { Brain, CheckCircle, XCircle, TrendingUp, TrendingDown, Zap, RefreshCw, Loader2, Star } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAppStore } from '../store';
import { getAdvancedChecklist, getPresets } from '../utils/api';

export default function AIChecklist() {
  const { user } = useUser();
  const { selectedPair, prices, activeStrategy } = useAppStore();
  const [direction, setDirection] = useState('long');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [checklistData, setChecklistData] = useState(null);
  const [cachedPreset, setCachedPreset] = useState(null);
  const [presetCacheTime, setPresetCacheTime] = useState(0);
  
  const currentPrice = prices?.find(p => p.symbol === selectedPair)?.bid || 0;
  
  // Get user ID from Clerk
  const userId = user?.id || null;
  
  // Cache preset for 30 seconds to avoid rate limiting
  const CACHE_DURATION = 30000; // 30 seconds
  
  // Get active preset with caching
  // PRIORITY: activeStrategy (from store) > cachedPreset > userPreset > default
  const getActivePreset = async () => {
    // PRIORITY 1: Use activeStrategy from global store (set by StrategiesPage)
    if (activeStrategy) {
      const normalized = String(activeStrategy).toUpperCase().trim();
      console.log('🎯 Checklist: Using activeStrategy from global store:', activeStrategy, '-> normalized:', normalized);
      return normalized;
    }
    
    const now = Date.now();
    
    // PRIORITY 2: Use cached preset if still valid
    if (cachedPreset && (now - presetCacheTime) < CACHE_DURATION) {
      console.log('Using cached preset:', cachedPreset);
      return cachedPreset;
    }
    
    // PRIORITY 3: Fetch fresh preset from backend
    let activePreset = 'ICT_CORE'; // Default
    try {
      const presetsResponse = await getPresets(userId);
      const presetsData = presetsResponse.data;
      
      // Find preset with is_active: true, or use current_preset
      if (presetsData.presets) {
        const activePresetObj = presetsData.presets.find(p => p.is_active === true);
        if (activePresetObj) {
          activePreset = activePresetObj.id;
          console.log('📋 Checklist: Found active preset from presets array:', activePresetObj.id, 'name:', activePresetObj.name);
        } else if (presetsData.current_preset) {
          activePreset = presetsData.current_preset;
          console.log('📋 Checklist: Using current_preset from response:', activePreset);
        }
      } else if (presetsData.current_preset) {
        activePreset = presetsData.current_preset;
        console.log('📋 Checklist: Using current_preset (no presets array):', activePreset);
      }
      
      // Normalize preset ID to ensure consistency (uppercase, trim)
      const originalPreset = activePreset;
      if (activePreset) {
        activePreset = String(activePreset).toUpperCase().trim();
      }
      console.log('📋 Checklist: Preset normalized:', activePreset, 'from:', originalPreset);
      
      // Cache the preset
      setCachedPreset(activePreset);
      setPresetCacheTime(now);
      console.log('Fetched and cached preset:', activePreset);
    } catch (presetErr) {
      console.warn('Error fetching preset, using default ICT_CORE:', presetErr);
      // If we have a cached preset, use it even if expired
      if (cachedPreset) {
        console.log('Using expired cached preset as fallback:', cachedPreset);
        return cachedPreset;
      }
    }
    
    return activePreset;
  };

  const loadChecklist = useCallback(async () => {
    if (!selectedPair) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Get active preset (with caching)
      const activePreset = await getActivePreset();
      
      console.log(`📋 Calling API: /api/ai/advanced-checklist`);
      console.log(`📋 Data:`, { symbol: selectedPair, direction, preset: activePreset, user_id: userId });
      
      const requestData = {
        symbol: selectedPair,
        direction: direction,
        preset: activePreset, // ← Add preset to request
        user_id: userId // ← Also pass user_id for backend
      };
      
      console.log('📋 Checklist Request:', JSON.stringify(requestData, null, 2));
      
      const response = await getAdvancedChecklist(requestData);
      
      console.log(`📋 Response status: ${response.status}`);
      
      const data = response.data;
      console.log('📋 Checklist Response:', {
        score: data.score,
        max_total: data.max_total,
        entry_threshold: data.entry_threshold,
        preset: data.preset,
        total_points: data.total_points
      });
      console.log('📋 Full response:', data);
      
      // Check if response contains an error
      if (data.error) {
        console.error('API returned error:', data.error);
        if (data.debug) {
          console.error('Debug info:', data.debug);
        }
        throw new Error(data.error);
      }
      
      // Handle existing setup response (different structure)
      if (data.setup_exists && data.existing_setup) {
        console.log('⚠️ Setup already exists for this symbol/direction');
        
        // Convert existing setup response to checklist format
        const convertedData = {
          score: data.percentage || data.original_analysis?.percentage || 0,
          percentage: data.percentage || data.original_analysis?.percentage || 0,
          rating: data.rating || data.original_analysis?.rating || 'UNKNOWN',
          summary: data.validation?.message || 'Setup already exists',
          reasons: data.original_analysis?.reasons || [],
          breakdown: data.original_analysis?.breakdown || [],
          timeframes: data.original_analysis?.timeframes || {},
          
          // Add existing setup info
          existing_setup: data.existing_setup,
          setup_exists: true,
          validation: data.validation,
          entry_timing: data.entry_timing,
          recommendation: data.recommendation
        };
        
        setChecklistData(convertedData);
        setError(''); // Clear any previous errors
        return;
      }
      
      // Validate response structure for new analysis
      if (typeof data.score === 'undefined' || !data.rating) {
        console.error('Invalid response structure:', data);
        throw new Error('Invalid response from server');
      }
      
      setChecklistData(data);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error loading checklist:', err);
      setError(err.message || 'Failed to load checklist. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPair, direction, userId, cachedPreset, presetCacheTime]);
  
  // Invalidate cache and reload checklist when strategy changes
  useEffect(() => {
    const handleStrategyChange = (event) => {
      const newPreset = event.detail?.preset;
      console.log('🔄 Strategy changed to:', newPreset, '- Invalidating cache and reloading checklist');
      setCachedPreset(null);
      setPresetCacheTime(0);
      
      // Reload checklist if a pair is selected
      if (selectedPair) {
        console.log('🔄 Reloading checklist with new preset:', newPreset);
        // Small delay to ensure cache is cleared
        setTimeout(() => {
          loadChecklist();
        }, 100);
      }
    };

    window.addEventListener('strategy-changed', handleStrategyChange);
    return () => {
      window.removeEventListener('strategy-changed', handleStrategyChange);
    };
  }, [selectedPair, loadChecklist]); // Include loadChecklist in dependencies

  useEffect(() => {
    if (selectedPair) {
      loadChecklist();
    }
  }, [selectedPair, direction]);
  
  const getRatingColor = (rating) => {
    switch(rating) {
      case 'EXCEPTIONAL': return 'text-purple-400 bg-purple-500/20';
      case 'EXCELLENT': return 'text-green-400 bg-green-500/20';
      case 'VERY GOOD': return 'text-blue-400 bg-blue-500/20';
      case 'GOOD': return 'text-yellow-400 bg-yellow-500/20';
      case 'FAIR': return 'text-orange-400 bg-orange-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };
  
  const getRatingStars = (rating) => {
    const counts = {
      'EXCEPTIONAL': 5,
      'EXCELLENT': 4,
      'VERY GOOD': 3,
      'GOOD': 2,
      'FAIR': 1,
      'WEAK': 0
    };
    return counts[rating] || 0;
  };
  
  if (!selectedPair) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400">
        <div className="text-center">
          <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Seleziona una coppia per vedere la checklist</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col bg-slate-900/50 backdrop-blur-xl">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-800/60 bg-slate-900/30 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3.5">
            <div className="p-2.5 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl shadow-medium shadow-cyan-500/20">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white tracking-tight">Advanced Checklist</h3>
              <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">Multi-TF Analysis · Score up to 175%+</p>
            </div>
          </div>
          
          <button
            onClick={loadChecklist}
            disabled={isLoading}
            className="p-2.5 hover:bg-slate-800/60 rounded-lg transition-smooth disabled:opacity-50 active:scale-95 focus-ring"
          >
            <RefreshCw className={`w-4 h-4 text-cyan-400 transition-transform ${isLoading ? 'animate-spin' : 'hover:rotate-180'}`} />
          </button>
        </div>
        
        {/* Direction Toggle */}
        <div className="flex gap-2.5">
          <button
            onClick={() => setDirection('long')}
            className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold transition-smooth active:scale-95 focus-ring ${
              direction === 'long'
                ? 'bg-emerald-500/20 text-emerald-300 border-2 border-emerald-500/40 shadow-soft shadow-emerald-500/10'
                : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800/80 hover:text-slate-300 border border-slate-700/40'
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5 inline mr-1.5" />
            LONG
          </button>
          <button
            onClick={() => setDirection('short')}
            className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold transition-smooth active:scale-95 focus-ring ${
              direction === 'short'
                ? 'bg-red-500/20 text-red-300 border-2 border-red-500/40 shadow-soft shadow-red-500/10'
                : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800/80 hover:text-slate-300 border border-slate-700/40'
            }`}
          >
            <TrendingDown className="w-3.5 h-3.5 inline mr-1.5" />
            SHORT
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative mb-5">
              <div className="absolute inset-0 bg-cyan-500/20 rounded-full blur-xl animate-pulse"></div>
              <Loader2 className="w-10 h-10 text-cyan-400 animate-spin relative z-10" />
            </div>
            <p className="text-sm text-slate-400 font-medium">Caricamento checklist...</p>
            <div className="mt-4 w-full max-w-xs h-1 bg-slate-800/50 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 rounded-full loading-shimmer"></div>
            </div>
          </div>
        )}
        
        {error && (
          <div className="bg-red-500/10 border-2 border-red-500/40 rounded-xl p-5 space-y-3 shadow-soft">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg flex-shrink-0">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1">
                <div className="text-red-300 font-bold mb-2 text-sm">Error Loading Checklist</div>
                <div className="text-red-400/90 text-sm leading-relaxed mb-3">{error}</div>
                <div className="mt-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                  <div className="text-xs text-red-400/70 space-y-1 leading-relaxed">
                    <div>• Check if MetaTrader 5 is running</div>
                    <div>• Verify the symbol is available in MT5</div>
                    <div>• Check browser console (F12) for details</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {checklistData && !isLoading && (
          <>
            {/* Existing Setup Alert */}
            {checklistData.setup_exists && checklistData.existing_setup && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-blue-400 mb-2">
                      ⚠️ Setup Already Exists
                    </h4>
                    <p className="text-xs text-slate-300 mb-3">
                      {checklistData.validation?.message || 'A setup already exists for this symbol and direction.'}
                    </p>
                    
                    {/* Setup Info */}
                    <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Created:</span>
                        <span className="text-white font-medium">
                          {checklistData.existing_setup.age_hours?.toFixed(1)}h ago
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Entry:</span>
                        <span className="text-white font-medium">
                          {checklistData.existing_setup.entry}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">SL / TP:</span>
                        <span className="text-white font-medium">
                          {checklistData.existing_setup.stop_loss} / {checklistData.existing_setup.take_profit}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">R:R:</span>
                        <span className="text-purple-400 font-medium">
                          1:{checklistData.existing_setup.risk_reward?.toFixed(1)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Score Change:</span>
                        <span className={`font-medium ${
                          checklistData.validation?.score_change > 0 ? 'text-green-400' :
                          checklistData.validation?.score_change < 0 ? 'text-red-400' : 'text-slate-400'
                        }`}>
                          {checklistData.validation?.original_score}% → {checklistData.validation?.current_score}%
                          ({checklistData.validation?.score_change > 0 ? '+' : ''}{checklistData.validation?.score_change}%)
                        </span>
                      </div>
                    </div>
                    
                    {/* Entry Timing */}
                    {checklistData.entry_timing && (
                      <div className={`mt-3 p-3 rounded-lg ${
                        checklistData.entry_timing.entry_now 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-yellow-500/10 border border-yellow-500/30'
                      }`}>
                        <div className="text-xs font-medium mb-1 ${
                          checklistData.entry_timing.entry_now ? 'text-green-400' : 'text-yellow-400'
                        }">
                          {checklistData.entry_timing.entry_now ? '🎯 ENTRA ADESSO!' : '⏳ ATTENDI'}
                        </div>
                        <div className="text-xs text-slate-300">
                          {checklistData.entry_timing.timing_message}
                        </div>
                      </div>
                    )}
                    
                  </div>
                </div>
              </div>
            )}
            
            {/* Main Score Summary */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-slate-300 mb-2 font-medium">
                    {checklistData.ml_available ? '🤖 AI-Enhanced Score' : 'Checklist Score'}
                  </div>
                  <div className="text-5xl font-bold text-white mb-2 tracking-tight">
                    {checklistData.score}%
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3.5 h-3.5 transition-all ${
                          i < getRatingStars(checklistData.rating)
                            ? 'text-yellow-400 fill-yellow-400 drop-shadow-sm'
                            : 'text-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                
                <div className={`px-4 py-2 rounded-full text-xs font-bold border shadow-soft ${getRatingColor(checklistData.rating)}`}>
                  {checklistData.rating}
                </div>
              </div>
              
              {/* ML Score Breakdown */}
              {checklistData.ml_available && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-blue-500/15 border border-blue-500/40 rounded-xl p-3 shadow-soft hover:border-blue-500/60 transition-smooth">
                    <div className="text-[10px] text-blue-300 mb-1.5 font-semibold uppercase tracking-wider">CHECKLIST</div>
                    <div className="text-xl font-bold text-blue-200 font-mono">{checklistData.checklist_score}%</div>
                  </div>
                  <div className="bg-purple-500/15 border border-purple-500/40 rounded-xl p-3 shadow-soft hover:border-purple-500/60 transition-smooth">
                    <div className="text-[10px] text-purple-300 mb-1.5 font-semibold uppercase tracking-wider">ML PREDICT</div>
                    <div className="text-xl font-bold text-purple-200 font-mono">{checklistData.ml_score}%</div>
                  </div>
                </div>
              )}
              
              {/* ML Stats */}
              {checklistData.ml_available && (
                <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-2 mb-2">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-purple-300">
                      🧠 Learned from {checklistData.ml_trades_learned} trades
                    </span>
                    <span className="text-purple-400 font-bold">
                      {(checklistData.ml_confidence * 100).toFixed(0)}% confident
                    </span>
                  </div>
                </div>
              )}
              
              {/* No ML yet */}
              {!checklistData.ml_available && checklistData.ml_reason && (
                <div className="bg-slate-700/30 border border-slate-600/30 rounded-lg p-2 mb-2">
                  <div className="text-[10px] text-slate-400">
                    🤖 {checklistData.ml_reason}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>Points: {checklistData.total_points}</span>
                <span>{selectedPair} · {direction.toUpperCase()}</span>
              </div>
              
              {checklistData.intraday_note && (
                <div className="mt-2 text-[10px] text-cyan-400 bg-cyan-500/10 rounded px-2 py-1">
                  ℹ️ {checklistData.intraday_note}
                </div>
              )}
            </div>
            
            {/* Timeframe Scores */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">Timeframe Analysis</h4>
              
              {Object.entries(checklistData.timeframes || {}).map(([tf, data]) => (
                <div key={tf} className={`glass-panel rounded-xl p-4 transition-smooth hover-lift ${
                  data.used_in_total === false 
                    ? 'opacity-60' 
                    : ''
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white">{tf}</span>
                      {data.used_in_total === false && (
                        <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded">
                          Not counted
                        </span>
                      )}
                      {data.used_in_total === true && (
                        <span className="text-[9px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">
                          Best intraday
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-mono text-cyan-400">
                      {data.total}/{data.max_possible}
                    </span>
                  </div>
                  
                  <div className="space-y-1">
                    {data.trend > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-emerald-400">✓ Trend</span>
                        <span className="text-slate-400">+{data.trend}</span>
                      </div>
                    )}
                    {data.at_aoi > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-emerald-400">✓ At AOI/Rejected</span>
                        <span className="text-slate-400">+{data.at_aoi}</span>
                      </div>
                    )}
                    {data.touching_ema > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-emerald-400">✓ Touching EMA</span>
                        <span className="text-slate-400">+{data.touching_ema}</span>
                      </div>
                    )}
                    {data.psychological_level > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-emerald-400">✓ Psychological Level</span>
                        <span className="text-slate-400">+{data.psychological_level}</span>
                      </div>
                    )}
                    {data.rejection_structure > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-emerald-400">✓ Rejection Structure</span>
                        <span className="text-slate-400">+{data.rejection_structure}</span>
                      </div>
                    )}
                    {data.candlestick_rejection > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-emerald-400">✓ Candlestick Rejection</span>
                        <span className="text-slate-400">+{data.candlestick_rejection}</span>
                      </div>
                    )}
                    {data.break_retest > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-emerald-400">✓ Break & Retest / H&S</span>
                        <span className="text-slate-400">+{data.break_retest}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Entry Signals */}
            {checklistData.entry_signals && Object.keys(checklistData.entry_signals).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Entry Signals</h4>
                
                <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg p-3 border border-cyan-500/30">
                  {Object.entries(checklistData.entry_signals).map(([key, signal]) => (
                    <div key={key} className="flex items-center justify-between mb-2 last:mb-0">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm text-white font-medium">
                          {key.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-cyan-400 font-mono text-sm">
                        +{signal.points}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Detailed Breakdown */}
            {checklistData.breakdown && checklistData.breakdown.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Detailed Breakdown</h4>
                
                <div className="space-y-1">
                  {checklistData.breakdown.map((item, idx) => (
                    <div key={idx} className="bg-slate-800/30 rounded px-3 py-2 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 font-mono">{item.tf}</span>
                        <span className="text-slate-300">{item.check}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400">{item.detail}</span>
                        <span className="text-emerald-400 font-mono">+{item.points}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
